import { Action } from 'util/domain';
import { CommonUtilsService } from 'util/services'

import {
	ActionToastMessage,
	OnQueueEmptyCallback,
	ProgressMessageOptions,
	ProgressToastMessage,
	SimpleToastMessage,
	ToastAction,
	ToastMessage,
	ToastMessageType,
	ToastProgressMode

} from '../domain';

export const DEFAULT_DELAY = 3000;
// Max number of messages to show at one time
export const MAX_DISPLAYED = 5;

/**
 * The ToastMessageQueue drives what messages are displayed at any given time.
 * The queue manages the priority of each message, when they are displayed and
 * when they are removed.
 */
export class ToastMessageQueueService {
	public static readonly $inject = [
		'$timeout',
		'utils',
		'gettextCatalog'
	];

	// Queue for simple and basic action messages
	private readonly queue: ToastMessage[] = [];
	// queue for long lived toast messages
	private readonly progressActionQueue: ProgressToastMessage[] = [];
	// messages that are currently displayed
	private readonly displayed: ToastMessage[] = [];

	// subscribers that are invoked each time the queues are emptied.
	private onEmptyCallbacks: OnQueueEmptyCallback[] = [];

	private readonly deboundedProcess: () => void;
	private readonly debouncedHandleEmptyQueue: () => void;

	// total number of messages queued
	public get totalQueued() {
		return this.queue.length + this.progressActionQueue.length;
	}

	/**
	 * The list of messages that are currently displayed
	 */
	public get messages(): ToastMessage[] {
		return this.displayed;
	}

	/**
	 * The total number of messages. This includes both queued and displayed messages.
	 */
	public get total(): number {
		return this.totalQueued + this.displayed.length;
	}

	constructor(
		private readonly $timeout: ng.ITimeoutService,
		private readonly utils: CommonUtilsService,
		private readonly gettextCatalog
	) {
		// process and handle empty queue are called when an item is removed. There is potential
		// for these functions to be called multiple times during a single update run. To prevent
		// this, use debounced versions of the methods so they only execute once
		this.deboundedProcess = utils.debounce(this.process.bind(this));
		this.debouncedHandleEmptyQueue = utils.debounce(this.handleEmptyQueue.bind(this));
	}

	/**
	 * Add a simple string message to the toast queue.
	 *
	 * @param message The message to display
	 * @param delay An optional timeout, in milliseconds, to hide the message
	 */
	public addSimple(message: string, delay?: number) {
		let toast: SimpleToastMessage = {
			type: ToastMessageType.simple,
			message,
			hideDelay: delay || DEFAULT_DELAY
		};
		this.queue.push(toast);
		this.deboundedProcess();
	}

	/**
	 * Show a simple message that has an associated action.
	 *
	 * @param message The message to display
	 * @param action Action for the message
	 * @param delay An optional timeout, in milliseconds, to hide the message
	 */
	public addAction(message: string, action: ToastAction, delay?: number) {
		let toast: ActionToastMessage = {
			type: ToastMessageType.action,
			message,
			action,
			hideDelay: delay || DEFAULT_DELAY
		};

		this.queue.push(toast);
		this.deboundedProcess();
	}

	/**
	 * Show a long lived message.
	 *
	 * @param message The message to display
	 * @param opts Message options, including a progress messure and action
	 * @returns A function that can be used to remove the message from the toast
	 */
	public addProgress(opts: ProgressMessageOptions): () => void {
		let toast: ProgressToastMessage;
		if (opts.progressFn) {
			toast = {
				type: ToastMessageType.progress,
				mode: ToastProgressMode.determinate,
				get progress() {
					return opts.progressFn();
				},
				get message() {
					return typeof opts.message === 'string' ? opts.message : opts.message();
				}
			};
		} else {
			toast = {
				type: ToastMessageType.progress,
				mode: ToastProgressMode.indeterminate,
				get message() {
					return typeof opts.message === 'string' ? opts.message : opts.message();
				}
			};
		}

		if (opts.cancelFn) {
			toast.action = {
				icon: 'close',
				tooltip: this.gettextCatalog.getString('Cancel'),
				fn: opts.cancelFn
			};
		}

		this.progressActionQueue.push(toast);
		this.process();

		return () => this.removeProgressToast(toast);
	}

	/**
	 * Add a function that will be invoked each time the instance's queue is emptied.
	 *
	 * @param cb The function to invoke
	 */
	public onEmpty(cb: OnQueueEmptyCallback): Action {
		this.onEmptyCallbacks.push(cb);

		return () => {
			let i = this.onEmptyCallbacks.indexOf(cb);
			if (i >= 0) {
				this.onEmptyCallbacks.splice(i, 1);
			}
		}
	}

	/**
	 * Helper for properly executing a message's actions. This method will guarantee that
	 * the message is removed from the view once the action is invoked.
	 */
	public executeAction(message: ActionToastMessage | ProgressToastMessage) {
		message.action.fn();
		this.clearMessage(message, true);
	}

	/**
	 * Remove a displayed message after the message's specified delay interval and process the queue.
	 * If the message is a long running progress message, it will be removed after the default delay interval.
	 */
	public clearMessage(message: ToastMessage, immediate = false): void {
		let delay = DEFAULT_DELAY;
		if (message.type !== ToastMessageType.progress && message.hideDelay) {
			delay = message.hideDelay;
		}

		if (immediate) {
			delay = 0;
		}

		this.$timeout(() => {
			let i = this.displayed.indexOf(message);
			if (i >= 0) {
				this.displayed.splice(i, 1);
			}
	
			this.deboundedProcess();
		}, delay);
	}

	private process(): void {
		if (this.total === 0) {
			this.debouncedHandleEmptyQueue();
		} else {
			while (this.displayed.length < MAX_DISPLAYED && this.totalQueued > 0) {
				let message = this.getNext();
				this.insert(message);
				if (message.type !== ToastMessageType.progress) {
					this.clearMessage(message);
				}
			}
		}
	}

	private getNext(): ToastMessage {
		// Progress messages take priority, since they represent processes that
		// are happening right now.
		if (this.progressActionQueue.length > 0) {
			return this.progressActionQueue.shift();
		} else {
			return this.queue.shift();
		}
	}

	private handleEmptyQueue(): void {
		this.onEmptyCallbacks.forEach(cb => cb());
	}

	private insert(message: ToastMessage) {
		// we don't really care too much about message ordering, except that
		// progress messages should be displayed before any other type of message
		if (message.type !== ToastMessageType.progress) {
			this.displayed.push(message);
		} else {
			let numProgressDisplayed = this.displayed.filter(m => m.type === ToastMessageType.progress).length;
			this.displayed.splice(numProgressDisplayed, 0, message);
		}
	}

	private removeProgressToast(toast: ProgressToastMessage) {
		// if the message is queued, simply remove it from the queue.
		// otherwise call clearMessage to remove it from the UI after a timeout
		let i = this.progressActionQueue.indexOf(toast);
		if (i >= 0) {
			this.progressActionQueue.splice(i, 1);
		} else {
			this.clearMessage(toast);
		}
	}

}


// WEBPACK FOOTER //
// ./src/sharebase/services/toastMessageQueueService.ts